import { useEffect, useMemo, useRef } from "react";
import { type QueryClient, useQueryClient } from "react-query";

import { useSyncAmpBoardTaskApprovalsMutation } from "src/graphql";
import { captureException } from "src/lib/monitoring";
import { boardQueryMatcher } from "src/pages/projects/board-cache";
import { useWorkflows } from "src/pages/projects/hooks";

const SYNC_DELAY_MS = 300;

type TaskLike = { id: string; status_id: string };

function isTaskLike(value: object): value is TaskLike {
  return (
    "id" in value &&
    typeof value.id === "string" &&
    "status_id" in value &&
    typeof value.status_id === "string" &&
    "project_id" in value
  );
}

export function tasksIn(data: unknown, found: TaskLike[] = []): TaskLike[] {
  if (Array.isArray(data)) {
    for (const item of data) tasksIn(item, found);
  } else if (data !== null && typeof data === "object") {
    if (isTaskLike(data)) found.push(data);
    for (const value of Object.values(data)) tasksIn(value, found);
  }
  return found;
}

/**
 * Syncs approval steps whenever the board reads tasks, since a rule can be met
 * without a new approval: a required reviewer is removed, or a member leaves.
 */
export function useSyncTaskApprovals(
  refetchBoard: (queryClient: QueryClient) => Promise<void>,
) {
  const queryClient = useQueryClient();
  const { workflows } = useWorkflows();
  const approvalStatusIds = useMemo(
    () =>
      new Set(
        workflows.flatMap((workflow) =>
          workflow.statuses
            .filter((status) =>
              status.reviewers.some((reviewer) => reviewer.required),
            )
            .map((status) => status.id),
        ),
      ),
    [workflows],
  );
  const refetchRef = useRef(refetchBoard);
  refetchRef.current = refetchBoard;

  useEffect(() => {
    if (approvalStatusIds.size === 0) return;
    const pending = new Set<string>();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const isBoardQuery = boardQueryMatcher(null);

    const flush = async () => {
      timer = undefined;
      const ids = [...pending];
      pending.clear();
      try {
        // The mutation hook would invalidate every query and refetch the board.
        const moved = await useSyncAmpBoardTaskApprovalsMutation.fetcher({
          ids,
        })();
        if (moved.syncAmpTaskApprovals.length > 0) {
          await refetchRef.current(queryClient);
        }
      } catch (error) {
        captureException(error);
      }
    };

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event?.type !== "queryUpdated" ||
        event.action.type !== "success" ||
        !isBoardQuery(event.query)
      )
        return;
      for (const task of tasksIn(event.query.state.data)) {
        if (approvalStatusIds.has(task.status_id)) pending.add(task.id);
      }
      if (pending.size > 0 && timer === undefined) {
        timer = setTimeout(() => void flush(), SYNC_DELAY_MS);
      }
    });
    return () => {
      unsubscribe();
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [approvalStatusIds, queryClient]);
}
